import { useAuthStore } from '@/stores/retailer/auth.js'
import country from '@/utilities/country.js'
import { isSalesChannelClosedError } from '@/utilities/helpers.js'
import { RETAILER_TYPE } from '@/constants/retailerType.js'
import { SALES_CHANNEL_TYPE } from '@/constants/salesChannels.js'

/**
 * The sales lines of the flex panel, each keyed by the sales channel that carries it.
 *
 * A flex retailer sells on the lines they hold the channel for, which is not necessarily the one
 * line their retailer type names — a second-hand car retailer given the tractor channel sells both.
 * The route metas below are gated the same way, so an entry that appears here can always be entered.
 *
 * Agriculture is not in this list on purpose: it is a panel rather than a line, its many channels
 * all funnel into one create page, and most agriculture retailers hold no channel at all.
 */
const FLEX_SALES_LINES = [
  {
    salesChannel: SALES_CHANNEL_TYPE.CONSUMER_GOODS,
    retailerType: RETAILER_TYPE.CONSUMER_GOODS,
    routeMetaFlag: 'allowForConsumerGoods',
    routeName: 'retailer.applications.consumer-goods.create',
    labelKey: 'views.layouts.consumer_goods_sale',
    icon: 'mdi mdi-cart',
    testId: 'retailer-consumer-goods-application'
  },
  {
    salesChannel: SALES_CHANNEL_TYPE.CAR_SALES,
    retailerType: RETAILER_TYPE.CAR_SALES,
    routeMetaFlag: 'allowForCarSales',
    routeName: 'retailer.applications.car-sales.create',
    labelKey: 'views.layouts.car_sale',
    icon: 'mdi mdi-car',
    testId: 'retailer-car-sales-application'
  },
  {
    salesChannel: SALES_CHANNEL_TYPE.IYI_FINANS_CAR_SALES,
    retailerType: RETAILER_TYPE.IYI_FINANS,
    routeMetaFlag: 'allowForIyiFinans',
    routeName: 'retailer.applications.iyi-finans-car-sales.create',
    labelKey: 'views.layouts.iyi_finans_sale',
    icon: 'mdi mdi-car',
    testId: 'retailer-iyi-finans-application'
  },
  {
    salesChannel: SALES_CHANNEL_TYPE.TRACTOR_SALES,
    retailerType: RETAILER_TYPE.CAR_SALES,
    routeMetaFlag: 'allowForTractorSales',
    routeName: 'retailer.applications.tractor-sales.create',
    labelKey: 'views.layouts.tractor_sale',
    icon: 'mdi mdi-tractor',
    testId: 'retailer-tractor-sales-application'
  }
]

/**
 * Helper for retailer type checking and decidin if the retailer is of type CONSUMER_GOODS
 */
export const Retailer = {
  /**
   * Returns the current retailer type
   * @returns {number|undefined}
   */
  type: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer?.type
  },

  /**
   * Returns the current retailer object
   * @returns {Object|undefined}
   */
  get: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer
  },

  /**
   * Is the retailer of type CONSUMER_GOODS?
   * @returns {boolean}
   */
  isConsumerGoods: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer?.type === RETAILER_TYPE.CONSUMER_GOODS
  },

  /**
   * Is the retailer not of type CONSUMER_GOODS?
   * @returns {boolean}
   */
  isNotConsumerGoods: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer?.type !== RETAILER_TYPE.CONSUMER_GOODS
  },

  /**
   * Is the retailer of type CAR_SALES?
   * @returns {boolean}
   */
  isCarSales: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer?.type === RETAILER_TYPE.CAR_SALES
  },

  /**
   * Is the retailer of type IYI_FINANS (zero-km car sales)?
   * @returns {boolean}
   */
  isIyiFinans: () => {
    const authStore = useAuthStore()
    return authStore.user?.retailer?.type === RETAILER_TYPE.IYI_FINANS
  },

  /**
   * Does the retailer hold this sales channel?
   *
   * Deliberately not the same question as the is*() type checks: those ask what the retailer *is*
   * (which panel they work in), this one asks what they are allowed to *sell*. A flex retailer's
   * type can only ever name one of their sales lines, so every question about starting a sale has
   * to be asked of the channels.
   *
   * @param {number} salesChannelId a SALES_CHANNEL_TYPE value
   * @returns {boolean}
   */
  hasSalesChannel: salesChannelId => {
    const authStore = useAuthStore()

    return (authStore.user?.retailer?.sales_channels ?? []).some(salesChannel => salesChannel.id === salesChannelId)
  },

  /**
   * May the retailer start a new sale on this sales channel?
   *
   * Asked of the auth payload's closed_sales_channels rather than of sales_channels[].is_active:
   * a flex retailer can reach a line by their type alone and hold no channel row for it, and the
   * backend folds that case into the same list. A closed line only stops new sales — the
   * applications already started on it stay reachable, which is why this is not a route meta.
   *
   * @param {number} salesChannelId a SALES_CHANNEL_TYPE value
   * @returns {boolean}
   */
  isSalesChannelOpen: salesChannelId => {
    const authStore = useAuthStore()

    return (authStore.user?.closed_sales_channels ?? []).includes(salesChannelId) === false
  },

  /**
   * Reload the user when the backend refused a request because a sales channel is closed.
   *
   * The user is fetched once per page load, so a channel closed later reaches the panel only as
   * this error. Reloading it refreshes closed_sales_channels, and the entry points of that line
   * turn disabled without a page reload. A failed reload already shows its own notification.
   *
   * @param {Object} err the rejected request
   * @returns {boolean} whether the error was the closed sales channel
   */
  refreshIfSalesChannelClosed: err => {
    if (isSalesChannelClosedError(err) === false) {
      return false
    }

    useAuthStore()
      .getUser(true)
      .catch(() => {})

    return true
  },

  /**
   * May the retailer enter this route to start a new sale?
   *
   * Only the create route of a flex sales line is ever closed; every other route, the agriculture
   * and Romania create pages included, answers true.
   *
   * @param {string} routeName
   * @returns {boolean}
   */
  isSalesApplicationRouteOpen: routeName => {
    const salesLine = FLEX_SALES_LINES.find(line => line.routeName === routeName)

    return salesLine === undefined || Retailer.isSalesChannelOpen(salesLine.salesChannel)
  },

  /**
   * Does the retailer hold both the second-hand car and the tractor sales channel?
   *
   * The one pair that starts an application by picking a line first. Deliberately asked of these
   * two channels rather than of how many lines the retailer holds, so that a retailer picking up
   * some other second line is not read as the same case.
   *
   * @returns {boolean}
   */
  hasCarAndTractorSalesChannels: () => {
    return (
      Retailer.hasSalesChannel(SALES_CHANNEL_TYPE.CAR_SALES) &&
      Retailer.hasSalesChannel(SALES_CHANNEL_TYPE.TRACTOR_SALES)
    )
  },

  /**
   * The flex sales lines this retailer may start beyond the one their type already opens.
   *
   * The type picks the panel and the headline sale in it; every further line the retailer holds
   * gets its own entry. Nothing is returned for an agriculture retailer: that panel has a single
   * create page which every one of its channels goes through.
   *
   * @returns {Array<{salesChannel: number, routeName: string, labelKey: string, icon: string, testId: string}>}
   */
  additionalSalesLines: () => {
    if (Retailer.isAgriculture()) {
      return []
    }

    const primaryRouteName = Retailer.salesApplicationRouteName()

    return FLEX_SALES_LINES.filter(
      salesLine => salesLine.routeName !== primaryRouteName && Retailer.hasSalesChannel(salesLine.salesChannel)
    )
  },

  /**
   * The flex sales line the "Yeni Başvuru" button opens on its own — the first line of
   * FLEX_SALES_LINES the retailer holds, falling back to the one their type names when no channel
   * row is attached to them.
   *
   * @returns {Object|undefined}
   */
  headlineSalesLine: () => {
    return (
      FLEX_SALES_LINES.find(salesLine => Retailer.hasSalesChannel(salesLine.salesChannel)) ??
      FLEX_SALES_LINES.find(salesLine => Retailer.type() === salesLine.retailerType)
    )
  },

  /**
   * The sales lines the "Yeni Başvuru" menu lists, the further ones first and the headline line
   * last — the order the design has them in. Kept next to the two calls it is built from so the
   * ordering intent stays with them.
   *
   * @returns {Array<Object>}
   */
  salesLines: () => {
    const headlineSalesLine = Retailer.headlineSalesLine()

    return headlineSalesLine ? [...Retailer.additionalSalesLines(), headlineSalesLine] : []
  },

  /**
   * Is the retailer of flex type?
   *
   * Defaults to the retailer the session is working at. Pass one to ask about a retailer the user
   * is not currently in — the panel switch asks about the membership it is about to open, and the
   * answer has to come from here so the list of flex types stays in one place.
   *
   * @param {Object} [retailer] anything carrying a `type`, a retailer or a membership
   * @returns {boolean}
   */
  isFlex: retailer => {
    const type = retailer ? retailer.type : useAuthStore().user?.retailer?.type

    return [RETAILER_TYPE.CONSUMER_GOODS, RETAILER_TYPE.CAR_SALES, RETAILER_TYPE.IYI_FINANS].includes(type)
  },

  /**
   * Is the retailer of a vehicle-sales type — CAR_SALES or IYI_FINANS?
   * They all sell a vehicle (car or tractor) on installments rather than agricultural inputs,
   * and share the same UI treatment (vehicle-specific document/collection flows, "customer"
   * wording instead of "farmer", etc.) — use this instead of chaining isCarSales()/isIyiFinans().
   * @returns {boolean}
   */
  isVehicleSales: () => {
    const authStore = useAuthStore()
    return [RETAILER_TYPE.CAR_SALES, RETAILER_TYPE.IYI_FINANS].includes(authStore.user?.retailer?.type)
  },

  /**
   * Is the retailer neither CAR_SALES nor IYI_FINANS (i.e. not a vehicle-sales retailer)?
   * @returns {boolean}
   */
  isNotAnyCarSales: () => {
    return Retailer.isVehicleSales() === false
  },

  /**
   * Is the retailer of type OTHER or TARFIN? (agriculture retailer)
   * @returns {boolean}
   */
  isAgriculture: () => {
    const authStore = useAuthStore()
    return [RETAILER_TYPE.OTHER, RETAILER_TYPE.TARFIN].includes(authStore.user?.retailer?.type)
  },

  /**
   * May the current retailer enter a route with this meta?
   *
   * Single source of truth for the allowFor* route metas — the router's global guard, the post-login
   * redirect check and the sidebar filter all ask this one question, so they can never drift apart.
   *
   * The metas are additive ("also let this sales line in"), never exclusivity markers:
   * - allowForAllRetailerTypes: true  -> line-neutral page, every retailer may enter
   * - a flex retailer needs the flag of one of the lines they work on; a retailer on several lines
   *   may enter a page opened to any one of them
   * - an agriculture retailer is the default audience, so it is opt-out: only a route whose own path
   *   is line-scoped (a flex sales application flow) closes itself off via allowForAgriculture: false
   * - an unknown retailer type stays unrestricted
   *
   * @param {Object} [meta] route meta
   * @returns {boolean}
   */
  canAccessRouteMeta: meta => {
    const routeMeta = meta || {}

    if (routeMeta.allowForAllRetailerTypes === true) {
      return true
    }

    // Asked before the sales lines, not after: the type is what picks the panel, so an agriculture
    // retailer stays in the agriculture panel even when a flex channel has been attached to them.
    // That is not hypothetical — a demo retailer of type OTHER carries the consumer goods channel,
    // and reading it as a sales line would shut them out of every agriculture page they have.
    if (Retailer.isAgriculture()) {
      return routeMeta.allowForAgriculture !== false
    }

    // Inside the flex panel a retailer is admitted by any sales line they work on, not only the one
    // their type names. Their own type still counts on its own so that a retailer whose channel row
    // is missing keeps the pages they have today.
    const openLines = FLEX_SALES_LINES.filter(
      salesLine => Retailer.type() === salesLine.retailerType || Retailer.hasSalesChannel(salesLine.salesChannel)
    )

    if (openLines.length > 0) {
      return openLines.some(salesLine => routeMeta[salesLine.routeMetaFlag] === true)
    }

    return true
  },

  /**
   * Resolve the "Tarfin'le Sat" sales-application create route name for the
   * current retailer. Single source of truth — every "+ Tarfin'le Sat" button
   * in the app must route through this helper so all entry points (sidebar,
   * dashboard, sales summary, empty-state caption, etc.) land on the same
   * create page regardless of where they were clicked.
   *
   * A flex retailer's headline sale is the first line of FLEX_SALES_LINES they hold, not the one
   * their type happens to name: a retailer selling both second-hand cars and tractors opens the
   * car sale from the unnamed button and reaches the tractor one through its own named entry.
   * Holding no channel at all falls back to the type, which keeps a retailer whose channel row is
   * missing on the page they have today.
   *
   * @returns {string}
   */
  salesApplicationRouteName: () => {
    if (country.is('ro')) {
      return 'ro.retailer.applications.create'
    }

    if (Retailer.isAgriculture()) {
      return 'retailer.application.create'
    }

    return Retailer.headlineSalesLine()?.routeName ?? 'retailer.application.create'
  }
}

export default Retailer
